// client/src/hooks/useAnalyticsFilters.js
import { useState, useEffect, useCallback, useMemo } from 'react'

/**
 * Custom hook to manage analytics filtering state with URL params and localStorage
 *
 * Features:
 * - Manages date filters, player filters, API request filters, admin user filters
 * - Syncs with URL query parameters
 * - Persists to localStorage
 * - Provides computed filter values
 *
 * @returns {Object} Filter state and handlers
 */
export default function useAnalyticsFilters() {
  // Filter state
  const [dateFilter, setDateFilterState] = useState('30') // Last 30 days by default
  const [selectedPlayerId, setSelectedPlayerIdState] = useState(null)
  const [playerSearchTerm, setPlayerSearchTerm] = useState('')
  const [hideApiRequests, setHideApiRequests] = useState(true) // Hide api_request events by default
  const [excludeAdminUsers, setExcludeAdminUsers] = useState(false)

  /**
   * Initialize filters from URL params and localStorage on mount
   */
  useEffect(() => {
    // Check URL parameters first
    const urlParams = new URLSearchParams(window.location.search)
    const playerIdFromUrl = urlParams.get('playerId')

    if (playerIdFromUrl) {
      setSelectedPlayerIdState(playerIdFromUrl)
      // Save to localStorage
      localStorage.setItem('analytics_selected_player_id', playerIdFromUrl)
    } else {
      // Check localStorage for last selected player
      const savedPlayerId = localStorage.getItem('analytics_selected_player_id')
      if (savedPlayerId) {
        setSelectedPlayerIdState(savedPlayerId)
      }
    }
  }, [])

  /**
   * Set date filter
   * @param {string} days - Number of days ('7', '30', '90')
   */
  const setDateFilter = useCallback((days) => {
    setDateFilterState(days)
  }, [])

  /**
   * Set selected player ID with URL and localStorage sync
   * @param {number|string|null} playerId - Player ID to filter by
   */
  const setSelectedPlayerId = useCallback((playerId) => {
    setSelectedPlayerIdState(playerId)
    setPlayerSearchTerm('')

    // Update URL parameter
    const url = new URL(window.location)
    if (playerId) {
      url.searchParams.set('playerId', playerId)
      // Save to localStorage
      localStorage.setItem('analytics_selected_player_id', playerId)
    } else {
      url.searchParams.delete('playerId')
      // Remove from localStorage
      localStorage.removeItem('analytics_selected_player_id')
    }
    window.history.replaceState(null, '', url.toString())
  }, [])

  /**
   * Clear player filter
   */
  const clearPlayerFilter = useCallback(() => {
    setSelectedPlayerId(null)
  }, [setSelectedPlayerId])

  /**
   * Get filtered players based on search term
   * @param {Array} players - Full list of players
   * @returns {Array} Filtered players (max 10)
   */
  const getFilteredPlayers = useCallback(
    (players) => {
      if (!playerSearchTerm) return players.slice(0, 10) // Show first 10 by default

      return players
        .filter((player) => {
          const fullName = `${player.first_name || ''} ${
            player.last_name || ''
          }`.toLowerCase()
          return (
            fullName.includes(playerSearchTerm.toLowerCase()) ||
            (player.user_id &&
              player.user_id.toString().includes(playerSearchTerm))
          )
        })
        .slice(0, 10)
    },
    [playerSearchTerm],
  )

  /**
   * Get selected player name
   * @param {Array} players - Full list of players
   * @returns {string|null} Player name or user ID
   */
  const getSelectedPlayerName = useCallback(
    (players) => {
      if (!selectedPlayerId) return null
      const player = players.find(
        (p) => p.user_id && p.user_id.toString() === selectedPlayerId,
      )
      if (player) {
        return (
          `${player.first_name || ''} ${player.last_name || ''}`.trim() ||
          `User ${player.user_id}`
        )
      }
      return `User ${selectedPlayerId}`
    },
    [selectedPlayerId],
  )

  /**
   * Get event filters object for API calls
   * @returns {Object} { excludeTag, excludeAdminUsers }
   */
  const getEventFilters = useCallback(() => {
    return {
      excludeTag: hideApiRequests ? 'api_request' : null,
      excludeAdminUsers,
    }
  }, [hideApiRequests, excludeAdminUsers])

  return useMemo(
    () => ({
      // State
      dateFilter,
      selectedPlayerId,
      playerSearchTerm,
      hideApiRequests,
      excludeAdminUsers,

      // Setters
      setDateFilter,
      setSelectedPlayerId,
      setPlayerSearchTerm,
      setHideApiRequests,
      setExcludeAdminUsers,
      clearPlayerFilter,

      // Computed values
      getFilteredPlayers,
      getSelectedPlayerName,
      getEventFilters,
    }),
    // Only include state values, not callbacks (already stable via useCallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dateFilter,
      selectedPlayerId,
      playerSearchTerm,
      hideApiRequests,
      excludeAdminUsers,
    ],
  )
}
